/**
 * Utility for managing ConfirmDialog state
 *
 * Usage:
 * ```svelte
 * <script lang="ts">
 * import { createConfirmDialog } from '$lib/confirmDialog.svelte';
 *
 * const confirmDialog = createConfirmDialog();
 *
 * async function handleDelete() {
 *   const confirmed = await confirmDialog.confirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure?',
 *     variant: 'danger'
 *   });
 *
 *   if (confirmed) {
 *     // proceed with deletion
 *   }
 * }
 * </script>
 *
 * <ConfirmDialog {...confirmDialog.props} />
 * ```
 */

type ConfirmOptions = {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'primary';
};

type ConfirmDialogState = {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  variant: 'danger' | 'warning' | 'primary';
  onConfirm: () => void;
  onClose: () => void;
};

export function createConfirmDialog() {
  let state = $state<ConfirmDialogState>({
    isOpen: false,
    title: 'Confirm Action',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'primary',
    onConfirm: () => {},
    onClose: () => {}
  });

  let resolvePromise: ((value: boolean) => void) | null = null;

  function confirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      resolvePromise = resolve;

      state.isOpen = true;
      state.title = options.title || 'Confirm Action';
      state.message = options.message;
      state.confirmText = options.confirmText || 'Confirm';
      state.cancelText = options.cancelText || 'Cancel';
      state.variant = options.variant || 'primary';

      state.onConfirm = () => {
        state.isOpen = false;
        resolvePromise?.(true);
      };

      state.onClose = () => {
        state.isOpen = false;
        resolvePromise?.(false);
      };
    });
  }

  return {
    confirm,
    get props() {
      return {
        isOpen: state.isOpen,
        title: state.title,
        message: state.message,
        confirmText: state.confirmText,
        cancelText: state.cancelText,
        variant: state.variant,
        onConfirm: state.onConfirm,
        onClose: state.onClose
      };
    }
  };
}
